/**
 * The staff directory: who can be assigned something, loaded once per session.
 *
 * Six surfaces want the same twenty rows at the same moment - the assignee
 * column, the task form, the time-entry filter, the project members strip, the
 * milestone owner and the roadmap head - so the request is shared rather than
 * repeated per mount. The cache is a promise, not a result: two pickers mounting
 * in the same frame both await the one request instead of racing two, which is
 * the same trick `useTtsVoices` uses for the voice list.
 *
 * There is no invalidation and no `forgetStaff`, on purpose. Nothing inside the
 * CRM creates a person or takes one off the list; that happens in web_admin,
 * behind a full page load. A refresh nobody can trigger from here would be dead
 * weight that reads like a promise this module does not keep - the call
 * `useCrmLabels` already makes for the label registry.
 *
 * A failed fetch leaves the list EMPTY and never throws at the caller. An
 * assignee column that cannot resolve names still has to draw its rows: a table
 * that dies because the directory is down is a far worse answer than a column of
 * question marks. The attempt clears itself, so the next screen to mount tries
 * again rather than inheriting the empty list for the rest of the session.
 */
import { useEffect, useState } from "react";
import { getStaff, type StaffUser } from "../../../lib/projects";

/** Re-exported so a screen needs one import for the hook and for the row it hands back. */
export type { StaffUser };

/**
 * The mark for an id that resolves to nobody.
 *
 * A literal question mark, and never a word. An unresolved id drawn as a blank
 * circle reads as a broken avatar, while "?" reads as a person we cannot name -
 * which is exactly what it is. It is not translated either: the string would
 * have to be right in whichever language the caller is in, and a hardcoded
 * English "Unknown" sitting in an Estonian table is worse than the mark.
 */
export const UNKNOWN_MARK = "?";

/**
 * The mark for a field that names nobody at all - no id was ever stored.
 *
 * Deliberately different from `UNKNOWN_MARK`: "nobody is on this" and "somebody
 * is on this and we cannot say who" are two different facts, and a reader who
 * sees the same glyph for both cannot tell a clean unassigned row from a broken
 * one. It is also a plain hyphen rather than an empty node, so a table cell
 * keeps its height instead of collapsing the row it sits in.
 */
export const NOBODY_MARK = "-";

interface Directory {
  staff: StaffUser[];
  byId: Map<string, StaffUser>;
}

const EMPTY: Directory = { staff: [], byId: new Map() };

let cached: Directory | null = null;
let inflight: Promise<Directory> | null = null;

/**
 * Active people first, then by name.
 *
 * Ordered here rather than in each picker because every surface wants the same
 * order: a deactivated person stays pickable - an old time entry still has to be
 * correctable, and a historic assignee still has to resolve to a name - but must
 * never look like a candidate for new work. The route sorts by name alone; it
 * has no reason to know what the pickers are for.
 */
function byRank(a: StaffUser, b: StaffUser): number {
  if (a.active !== b.active) return a.active ? -1 : 1;
  return a.name.localeCompare(b.name);
}

function load(): Promise<Directory> {
  inflight ??= getStaff()
    .then((r) => {
      const staff = [...r.rows].sort(byRank);
      // One Map instance for every consumer, so a caller that memoises on `byId`
      // is not recomputing on each render of a directory that never changes.
      cached = { staff, byId: new Map(staff.map((s) => [s.id, s])) };
      return cached;
    })
    .catch(() => {
      inflight = null;
      return EMPTY;
    });
  return inflight;
}

export interface StaffDirectory {
  staff: StaffUser[];
  byId: Map<string, StaffUser>;
  /** True only until the first answer arrives - a failed fetch is done, not loading. */
  loading: boolean;
}

export function useStaff(): StaffDirectory {
  // Seeded from the module snapshot, so the second screen to ask draws names on
  // its first frame instead of flashing a column of "?" for one microtask.
  const [dir, setDir] = useState<Directory>(() => cached ?? EMPTY);
  const [loading, setLoading] = useState(cached === null);

  useEffect(() => {
    if (cached) return;
    let alive = true;
    void load().then((next) => {
      if (!alive) return;
      setDir(next);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { staff: dir.staff, byId: dir.byId, loading };
}

/**
 * The display name for an id, and the fallback when there is none.
 *
 * Falls through to the email before it gives up: a real membership whose user
 * never typed a name is still somebody the reader can identify, and "?" would be
 * throwing away an answer we hold. Only an id with nothing behind it - a person
 * removed from the host site, or an id from a system that is no longer ours -
 * gets `UNKNOWN_MARK`.
 */
export function staffLabel(byId: Map<string, StaffUser>, id: string | null | undefined): string {
  const person = id ? byId.get(id) : undefined;
  return person?.name.trim() || person?.email.trim() || UNKNOWN_MARK;
}
